import {
  createUserWithEmailAndPassword,
  deleteUser,
  GoogleAuthProvider,
  OAuthProvider,
  sendPasswordResetEmail,
  signInWithCredential,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  linkWithCredential,
  type AuthCredential,
  type User
} from "firebase/auth";
import { getFunctions, httpsCallable, type Functions } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { auth, db, firebaseApp, functions } from "../firebase/client";
import { functionsRegion } from "../config/env";
import { isNative, platform } from "../utils/platform";
import type { UserProfile } from "../types/domain";

let pendingSocialCredential: AuthCredential | null = null;
let pendingAppleDisplayName = "";
const appleDisplayNames = new Map<string, string>();

export function appleProfileNames(user: User): { firstName: string; lastName: string } {
  const names = (appleDisplayNames.get(user.uid) || user.displayName || "").trim().split(/\s+/);
  return { firstName: names[0] || "", lastName: names.slice(1).join(" ") };
}

async function preserveAppleName(user: User, displayName: string): Promise<void> {
  if (!displayName || user.displayName) return;
  appleDisplayNames.set(user.uid, displayName);
  // Keep the first-authorization name for profile creation even if this write fails.
  await updateProfile(user, { displayName }).catch(() => undefined);
}

function normalizeUsername(value: string): string {
  return value.trim().normalize("NFKC").toLocaleLowerCase("en-US");
}

function isAccountExistsError(error: unknown): boolean {
  return String((error as { code?: string })?.code || "").includes("account-exists-with-different-credential");
}

async function finishUsernameLogin(user: User): Promise<User> {
  if (!pendingSocialCredential) return user;
  const credential = pendingSocialCredential;
  pendingSocialCredential = null;
  try {
    await linkWithCredential(user, credential);
    await preserveAppleName(user, pendingAppleDisplayName);
    pendingAppleDisplayName = "";
  } catch (error) {
    pendingSocialCredential = credential;
    throw error;
  }
  return user;
}

function functionsErrorCode(error: unknown): string {
  return String((error as { code?: string })?.code || "").toLowerCase();
}

function shouldRetryUsernameFunction(error: unknown): boolean {
  const code = functionsErrorCode(error);
  return (
    code.includes("functions/not-found") ||
    code.includes("functions/unavailable") ||
    code.includes("functions/internal") ||
    code.includes("functions/unknown")
  );
}

function usernameLoginFunctions(): Functions[] {
  const regions = Array.from(new Set([functionsRegion, "europe-west1", "us-central1"]));
  return regions.map((region) => region === functionsRegion ? functions : getFunctions(firebaseApp, region));
}

export async function signInWithUsername(username: string, password: string): Promise<User> {
  const normalized = normalizeUsername(username);
  let firstRetryableError: unknown = null;

  for (const functionsInstance of usernameLoginFunctions()) {
    const callable = httpsCallable<{ username: string; password: string }, { customToken: string }>(
      functionsInstance,
      "loginWithUsername"
    );
    try {
      const result = await callable({ username: normalized, password });
      const customToken = String(result.data?.customToken || "").trim();
      if (!customToken) throw new Error("USERNAME_LOGIN_TOKEN_MISSING");
      const user = (await signInWithCustomToken(auth, customToken)).user;
      return finishUsernameLogin(user);
    } catch (error) {
      if (!shouldRetryUsernameFunction(error)) throw error;
      if (firstRetryableError === null) firstRetryableError = error;
    }
  }

  throw firstRetryableError ?? new Error("USERNAME_LOGIN_UNAVAILABLE");
}

export async function signInWithEmail(email: string, password: string): Promise<User> {
  return (await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password)).user;
}

export async function registerWithPassword(input: {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  password: string;
}): Promise<User> {
  const check = httpsCallable<{ username: string }, { available: boolean }>(functions, "checkUsernameAvailability");
  const normalized = normalizeUsername(input.username);
  const availability = await check({ username: normalized });
  if (!availability.data.available) throw new Error("USERNAME_TAKEN");

  const credential = await createUserWithEmailAndPassword(auth, input.email.trim().toLowerCase(), input.password);
  try {
    const claim = httpsCallable<
      { firstName: string; lastName: string; username: string },
      { profile: UserProfile }
    >(functions, "claimUsernameAndProfile");
    await claim({
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      username: input.username.trim()
    });
    return credential.user;
  } catch (error) {
    await deleteUser(credential.user).catch(() => undefined);
    throw error;
  }
}

export async function signInGoogle(): Promise<User> {
  if (!isNative()) {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    try {
      return (await signInWithPopup(auth, provider)).user;
    } catch (error) {
      if (isAccountExistsError(error)) {
        pendingSocialCredential = GoogleAuthProvider.credentialFromError(error as never);
        throw new Error("ACCOUNT_LINK_REQUIRED");
      }
      throw error;
    }
  }

  const result = await FirebaseAuthentication.signInWithGoogle({
    skipNativeAuth: true,
    scopes: ["email", "profile"]
  });
  const idToken = result.credential?.idToken;
  const accessToken = result.credential?.accessToken;
  if (!idToken && !accessToken) throw new Error("GOOGLE_TOKEN_MISSING");
  const credential = GoogleAuthProvider.credential(idToken ?? null, accessToken ?? null);
  try {
    return (await signInWithCredential(auth, credential)).user;
  } catch (error) {
    if (isAccountExistsError(error)) {
      pendingSocialCredential = credential;
      throw new Error("ACCOUNT_LINK_REQUIRED");
    }
    throw error;
  }
}

// The Apple identity already supplies any available name/email. The unique
// internal username is generated automatically and remains editable later.
// Repeated or interrupted sign-ins must not create duplicate profiles.
const appleProfileInFlight = new Map<string, Promise<void>>();
export function ensureAppleProfile(user: User): Promise<void> {
  const pending = appleProfileInFlight.get(user.uid);
  if (pending) return pending;
  const task = (async () => {
    if (!user.providerData.some((provider) => provider.providerId === "apple.com")) {
      throw new Error("APPLE_ACCOUNT_REQUIRED");
    }
    const profileSnapshot = await getDoc(doc(db, "users", user.uid));
    if (profileSnapshot.exists() && String(profileSnapshot.data().usernameNormalized || "").trim()) return;

    // Firebase UIDs are stable; a long alphanumeric prefix is collision-resistant.
    const normalizedUid = user.uid.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    const username = `traveler_${normalizedUid.slice(0, 21)}`;
    if (username.length < 12) throw new Error("APPLE_USERNAME_UNAVAILABLE");
    const names = appleProfileNames(user);
    const claim = httpsCallable<
      { firstName: string; lastName: string; username: string },
      { profile: UserProfile }
    >(functions, "claimUsernameAndProfile");
    await claim({ ...names, username });
  })();
  appleProfileInFlight.set(user.uid, task);
  void task.finally(() => {
    if (appleProfileInFlight.get(user.uid) === task) appleProfileInFlight.delete(user.uid);
  }).catch(() => undefined);
  return task;
}

export async function signInApple(): Promise<User> {
  if (platform() !== "ios") throw new Error("APPLE_IOS_ONLY");

  const result = await FirebaseAuthentication.signInWithApple({
    skipNativeAuth: true
  });
  const idToken = result.credential?.idToken;
  const nonce = result.credential?.nonce;
  if (!idToken) throw new Error("APPLE_TOKEN_MISSING");
  const provider = new OAuthProvider("apple.com");
  const credential = provider.credential({ idToken, rawNonce: nonce ?? undefined });
  let user: User;
  try {
    user = (await signInWithCredential(auth, credential)).user;
  } catch (error) {
    if (isAccountExistsError(error)) {
      pendingSocialCredential = credential;
      pendingAppleDisplayName = String(result.user?.displayName || "").trim();
      throw new Error("ACCOUNT_LINK_REQUIRED");
    }
    throw error;
  }
  await preserveAppleName(user, String(result.user?.displayName || "").trim());
  await ensureAppleProfile(user);
  return user;
}

export async function sendReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email.trim().toLowerCase());
}

export async function logout(): Promise<void> {
  pendingSocialCredential = null;
  pendingAppleDisplayName = "";
  appleDisplayNames.clear();
  appleProfileInFlight.clear();
  await Promise.allSettled([
    signOut(auth),
    isNative() ? FirebaseAuthentication.signOut() : Promise.resolve()
  ]);
}

export function currentUser(): User | null {
  return auth.currentUser;
}
